import React, { useEffect, useState } from 'react';

import infracoes_api from '../../../../api/infracoes_api';
import { montarSrcDocImpressao } from '../../../../helpers/htmlImpressao';

// Components
import CartaoFicha from './CartaoFicha';
import RodapeEmissor from './RodapeEmissor';
import AvisoItemSemConteudo from './AvisoItemSemConteudo';

// O navegador nao quebra o conteudo de um iframe entre folhas: documento mais alto que uma A4 saia
// cortado na impressao (a Cota Defesa parava no meio) e a altura medida a mais deixava folha em
// branco. O documento continua vindo em iframe, mas fatiado em janelas de uma folha, um CartaoFicha
// por fatia — mesmo formato das paginas do ItemPdf.

// Largura fixa na tela e na impressao. Se variasse entre as duas, o texto refluiria, a altura
// medida aqui nao valeria na folha e as fatias sairiam deslocadas.
const LARGURA_DOCUMENTO_PX = 750;

// Altura util de uma folha A4 (29,7cm = 1123px a 96dpi) menos cabecalho, rodape e margens do
// CartaoFicha — teto de ~25cm. Acima de 1000px cabecalho e rodape do CartaoFicha nao cabem mais na A4.
const ALTURA_FATIA_PX = 950;

const SANDBOX_IFRAME = "allow-same-origin";

// Documento que passa da folha por pouco — a ficha da CTR e o caso tipico — sai reduzido numa folha
// so, em vez de partido em duas. Abaixo desta escala a leitura sofre e vale mais paginar.
const ESCALA_MINIMA = 0.7;

const SELETOR_BLOCOS = "p,div,table,tr,li,h1,h2,h3,h4,h5,h6,img,section,article,blockquote,pre";

// Iframe usado so para medir: fora da tela e com altura 0, para que scrollHeight devolva a altura
// real do conteudo em vez da altura do proprio iframe.
const ESTILO_MEDIDOR = {
    position: "absolute", left: "-10000px", top: 0, width: LARGURA_DOCUMENTO_PX,
    height: 0, border: "none", visibility: "hidden"
};

function medirAltura(doc) {
    return Math.max(
        doc.documentElement?.scrollHeight ?? 0,
        doc.documentElement?.offsetHeight ?? 0,
        doc.body?.scrollHeight ?? 0,
        doc.body?.offsetHeight ?? 0
    );
}

// Alturas onde da para cortar sem partir um bloco ao meio: o topo de cada elemento que cabe inteiro
// dentro de uma fatia.
function obterFronteiras(doc) {
    const topoDoc = doc.body?.getBoundingClientRect().top ?? 0;

    const topos = Array.from(doc.body?.querySelectorAll(SELETOR_BLOCOS) ?? [])
        .filter(elemento => elemento.offsetHeight > 0 && elemento.offsetHeight <= ALTURA_FATIA_PX)
        .map(elemento => Math.round(elemento.getBoundingClientRect().top - topoDoc))
        .filter(topo => topo > 0);

    return Array.from(new Set(topos)).sort((a, b) => a - b);
}

function calcularFatias(doc, alturaTotal) {
    const fronteiras = obterFronteiras(doc);
    const fatias = [];
    let inicio = 0;

    while (inicio < alturaTotal) {
        const inicioDaFatia = inicio;
        const limite = inicioDaFatia + ALTURA_FATIA_PX;

        if (limite >= alturaTotal) {
            fatias.push({ inicio: inicioDaFatia, altura: alturaTotal - inicioDaFatia });
            break;
        }

        // Ultima fronteira que ainda cabe na fatia. Documento sem fronteira nenhuma no trecho (um
        // bloco unico mais alto que a folha) corta na altura cheia: cortar e melhor que perder o
        // resto do documento, que era o defeito original.
        const fronteirasNaFatia = fronteiras.filter(topo => topo > inicioDaFatia && topo <= limite);
        const corte = fronteirasNaFatia.length ? fronteirasNaFatia[fronteirasNaFatia.length - 1] : limite;

        fatias.push({ inicio: inicioDaFatia, altura: corte - inicioDaFatia });
        inicio = corte;
    }

    return fatias;
}

// Documento que cabe numa folha com uma reducao leve sai inteiro e escalado; o resto e fatiado.
function calcularPaginacao(doc, alturaTotal) {
    if (!alturaTotal) return { escala: 1, fatias: [] };

    if (alturaTotal > ALTURA_FATIA_PX && alturaTotal * ESCALA_MINIMA <= ALTURA_FATIA_PX)
        return { escala: ALTURA_FATIA_PX / alturaTotal, fatias: [{ inicio: 0, altura: alturaTotal }] };

    return { escala: 1, fatias: calcularFatias(doc, alturaTotal) };
}

export default function ItemHtml({ hash, parametrizacaoHash, titulo, numeroInfracao, onReady }) {
    const [html, setHtml] = useState(null);
    const [erro, setErro] = useState(null);
    const [alturaTotal, setAlturaTotal] = useState(0);
    const [fatias, setFatias] = useState([]);
    const [escala, setEscala] = useState(1);

    useEffect(() => {
        infracoes_api.GetImpressaoItemHtml(hash, parametrizacaoHash)
            .then(({ data }) => {
                if (data?.html)
                    setHtml(data.html);
                else
                    onReady();
            })
            .catch(error => {
                setErro(error?.message ?? "Não foi possível gerar este documento.");
                onReady();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hash, parametrizacaoHash]);

    const medir = (event) => {
        const doc = event.target.contentWindow?.document;
        if (!doc) {
            onReady();
            return;
        }

        const remedir = () => {
            const altura = medirAltura(doc);
            if (!altura) return;

            const paginacao = calcularPaginacao(doc, altura);

            setAlturaTotal(altura);
            setFatias(paginacao.fatias);
            setEscala(paginacao.escala);
        };

        remedir();
        onReady();

        // Documento de integracao externa (ficha da CTR) traz imagens que terminam de carregar
        // depois do load do iframe: sem remedir, a ultima fatia perderia o fim do conteudo.
        Array.from(doc.images ?? []).forEach(imagem => {
            if (!imagem.complete) imagem.addEventListener("load", remedir, { once: true });
        });

        if (typeof ResizeObserver !== "undefined" && doc.body)
            new ResizeObserver(remedir).observe(doc.body);
    };

    if (erro) return <AvisoItemSemConteudo titulo={titulo} mensagem={erro} />;

    if (!html) return null;

    const srcDoc = montarSrcDocImpressao(html);

    return (
        <>
            {fatias.map((fatia, indice) => (
                <CartaoFicha key={indice} titulo={titulo}>
                    <div style={{ height: fatia.altura * escala, width: LARGURA_DOCUMENTO_PX * escala, maxWidth: "100%", overflow: "hidden", margin: "0 auto" }}>
                        <iframe
                            title={`${titulo} - página ${indice + 1}`}
                            srcDoc={srcDoc}
                            sandbox={SANDBOX_IFRAME}
                            scrolling="no"
                            style={{ width: LARGURA_DOCUMENTO_PX, height: alturaTotal, border: "none", display: "block", marginTop: -fatia.inicio, transform: `scale(${escala})`, transformOrigin: "top left" }}
                        />
                    </div>
                    <RodapeEmissor numeroInfracao={numeroInfracao} />
                </CartaoFicha>
            ))}

            <iframe
                title={`${titulo} (medição)`}
                className="noPrint"
                aria-hidden="true"
                srcDoc={srcDoc}
                sandbox={SANDBOX_IFRAME}
                scrolling="no"
                style={ESTILO_MEDIDOR}
                onLoad={medir}
            />
        </>
    );
}
